import React, { useEffect, useRef, useMemo } from "react"
import * as d3 from "d3"

import "./trajectoryMap.scss"
import type { CourtField } from "@/constants/sportConfig"

const BACKGROUND_IMAGE_URL = "/images/verticalGround.svg"

export default function TrajectoryMap({
  trajectoryData,
  pathColor,
  width,
  height,
  fieldBounds,
  backgroundSvg = BACKGROUND_IMAGE_URL,
  courtField = { left: 0, top: 0, right: 1, bottom: 1 },
}: {
  trajectoryData: any
  pathColor: string
  width: number
  height: number
  fieldBounds: any
  backgroundSvg?: string
  // 이미지 안 실제 경기장 영역(0~1). 기본 full-bleed = 기존 동작. (fieldBounds=데이터 도메인과 다름)
  courtField?: CourtField
}) {
  const mapRef = useRef(null)
  const markerId = useMemo(() => `arrowhead-${Math.random().toString(36).slice(2, 9)}`, [])

  const fieldLeft = courtField.left * width
  const fieldRight = courtField.right * width
  const fieldTop = courtField.top * height
  const fieldBottom = courtField.bottom * height

  useEffect(() => {
    if (!mapRef.current || !trajectoryData.length) return

    const svg = d3.select(mapRef.current)
    svg.selectAll("*").remove()

    svg.attr("width", width).attr("height", height)

    const xScale = d3
      .scaleLinear()
      .domain([fieldBounds.minX, fieldBounds.maxX])
      .range([fieldLeft, fieldRight])

    const yScale = d3
      .scaleLinear()
      .domain([fieldBounds.minY, fieldBounds.maxY])
      .range([fieldTop, fieldBottom])

    const defs = svg.append("defs")
    defs
      .append("marker")
      .attr("id", markerId)
      .attr("markerWidth", 10)
      .attr("markerHeight", 10)
      .attr("refX", 6)
      .attr("refY", 5)
      .attr("orient", "auto")
      .attr("markerUnits", "strokeWidth")
      .append("polygon")
      .attr("points", "0,1 2,5 0,9 10,5")
      .attr("fill", pathColor)

    const trajectoryGroup = svg.append("g").attr("class", "trajectories")

    trajectoryGroup
      .selectAll(".trajectory")
      .data(trajectoryData)
      .enter()
      .append("g")
      .attr("class", "trajectory")
      .style("cursor", "pointer")
      .each(function (d, i) {
        const group = d3.select(this)
        const trajectory = d.trajectory || []

        if (trajectory.length === 0) {
          return
        }

        if (trajectory.length === 1) {
          const point = trajectory[0]
          group
            .append("circle")
            .attr("cx", xScale(point.y))
            .attr("cy", yScale(point.x))
            .attr("r", 2)
            .attr("fill", pathColor)
            .attr("opacity", 0.7)

          if (d.value) {
            group
              .append("text")
              .attr("x", xScale(point.y) - 10)
              .attr("y", yScale(point.x) - 10)
              .attr("font-size", "12px")
              .attr("font-weight", "bold")
              .attr("fill", pathColor)
              .attr("text-anchor", "middle")
              .attr("dominant-baseline", "central")
              .text(d.value)
          }

          return
        }

        const line = d3
          .line()
          .x((point) => xScale(point.y))
          .y((point) => yScale(point.x))
          .curve(d3.curveCatmullRom.alpha(0.5))

        group
          .append("path")
          .datum(trajectory)
          .attr("d", line)
          .attr("fill", "none")
          .attr("stroke", pathColor)
          .attr("stroke-width", 2)
          .attr("stroke-linecap", "round")
          .attr("stroke-linejoin", "round")
          .attr("marker-end", `url(#${markerId})`)

        if (trajectory.length > 2) {
          group
            .selectAll(".waypoint")
            .data(trajectory.slice(1, -1))
            .enter()
            .append("circle")
            .attr("class", "waypoint")
            .attr("cx", (point) => xScale(point.y))
            .attr("cy", (point) => yScale(point.x))
            .attr("r", 1.5)
            .attr("fill", pathColor)
            .attr("opacity", 0.7)
        }

        if (d.value) {
          const startPoint = trajectory[0]
          const secondPoint = trajectory[1] || startPoint

          const dx = secondPoint.y - startPoint.y
          const dy = secondPoint.x - startPoint.x
          const length = Math.sqrt(dx * dx + dy * dy) || 1
          const unitX = dx / length
          const unitY = dy / length

          const labelX = xScale(startPoint.y) - unitX * 10
          const labelY = yScale(startPoint.x) - unitY * 10

          group
            .append("text")
            .attr("x", labelX)
            .attr("y", labelY + 1)
            .attr("font-size", "12px")
            .attr("font-weight", "bold")
            .attr("fill", pathColor)
            .attr("text-anchor", "middle")
            .attr("dominant-baseline", "central")
            .text(d.value)
        }
      })
  }, [
    trajectoryData,
    pathColor,
    width,
    height,
    fieldBounds,
    markerId,
    fieldLeft,
    fieldRight,
    fieldTop,
    fieldBottom,
  ])

  return (
    <div className="TrajectoryMap">
      <div
        className="TrajectoryMapContainer"
        style={{
          width: width + "px",
          height: height + "px",
        }}>
        <img className="trajectory-map-background" src={backgroundSvg} alt="BACKGROUND_IMAGE_URL" />
        <svg className="trajectory-map-stroke" ref={mapRef}></svg>
      </div>
    </div>
  )
}
